import { useEffect, useState } from "react";
import { 
    useForm, 
    useWatch,
    type SubmitHandler 
} from "react-hook-form"
import { 
    useNavigate, 
    useParams, 
    useLoaderData,
    redirect
} from "react-router-dom";
import type { LoaderFunctionArgs } from "react-router-dom";
import Markdown from "react-markdown"

type FormFields = {
    // id: number,
    title: string;
    date: string;
    time: string;
    location: string;
    cost: string;
    image: FileList;
    archived: boolean;
    details: string;
    deleted: boolean;
    url_slug: string;
}

type EventData = {
    id?: string;
    title: string;
    date: string;
    time: string;
    location: string;
    cost: string;
    image: string;
    archived: boolean;
    details: string;
    deleted: boolean;
    url_slug: string;
};

export async function Loader({params}: LoaderFunctionArgs): Promise<EventData | null> {
    
    const {eventID} = params

    if (!eventID){
        const response = await fetch(`${import.meta.env.VITE_API_URL}/verify`, {
            method: "GET",
            credentials: 'include',    
        })
        if(!response.ok){
            throw redirect('/admin')
        }
        return null
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/eventsBE/${eventID}`, {
        credentials: 'include'
    })
        if(!response.ok) {
            throw new Error('Failed to fetch events');
        }
        const [eventData] = await response.json();
        return eventData
    } catch (error) {
        console.error('Error loading event:', error)
        return null
    }
}

export default function NewPost() {
    const [loading, setLoading] = useState(true)
    const [textArea, setTextArea]= useState("")
    const loaderData = useLoaderData() as EventData | null;
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [previewSrc, setPreviewSrc] = useState('');

    

    useEffect(() => {
        if (!imageFile) return;

        const reader = new FileReader();

        reader.onloadend = () => {
        setPreviewSrc(reader.result as string);
        };

        reader.readAsDataURL(imageFile);
    }, [imageFile]);

    const handleImageChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const files = e.target.files;
        if (!files || !files.item(0)) return;

        setImageFile(files.item(0));
    };

    const navigate = useNavigate();
    const params = useParams()
    
    function collectTextArea(e: any){
        e.preventDefault()
        setTextArea(e.target.value)
    }
    
    const eventData = loaderData ?? {
        id: '',
        title: '',
        date: '',
        time: '',
        location: '',
        cost: '',
        image: '',
        archived: false,
        details: '',
        deleted: false,
        url_slug: ''
    };

    const { image, ...data } = eventData;

    async function deletePost(id: string){
        console.log("running...")
        
        const response = await fetch(`${import.meta.env.VITE_API_URL}/delete`, {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({"id": id})
        })

        if (response.ok) {
                alert('Event delete successfully')
                navigate('/admin/posts')
        } else {
            alert('Error deleting event')
        }
    
    }


    const { 
        register,
        handleSubmit,
        reset,
        setError, 
        control,
        formState: {errors, isSubmitting } 
    } = useForm<FormFields>({
        defaultValues: async () => {
            setLoading(false);
            if (data) {
                return {
                    title: data.title,
                    date: data.date,
                    time: data.time,
                    location: data.location,
                    cost: data.cost,
                    image: undefined as any,
                    archived: data.archived,
                    details: data.details,
                    deleted: false,
                    url_slug: data.url_slug
                    }
            } else {
                return {
                    title: '',
                    date: '',
                    time: '',
                    location: '',
                    cost: '',
                    image: undefined as any,
                    archived: false,
                    details: '# Example header',
                    deleted: false,
                    url_slug: ''
                }
            }
            
    }});

    const archivedValue = useWatch({control, name: "archived", defaultValue: false,})

     useEffect(( )=> {
        if (!params.eventID) {
        // Reset to empty values when navigating to new event
        reset({
            // id: 0,
            title: '',
            date: '',
            time: '',
            location: '',
            cost: '',
            image: undefined as any,
            archived: false,
            details: "# Example Header",
            url_slug: ''
        });
    }},[params.eventID, reset])

    const onSubmit: SubmitHandler<FormFields> = async (formData) => {
        const imageFile = formData.image?.[0]
        if(params.eventID) {
            try {
                const eventPayload: any = {
                    ...formData, 
                    id: params.eventID,
                    archived: formData.archived,
                    deleted: false
                }
                
                if(imageFile){
                    eventPayload.image = imageFile.name;
                } else {
                    eventPayload.image = image
                }
                
                const response = await fetch(`${import.meta.env.VITE_API_URL}/editEvent/${params.eventID}`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(eventPayload)

                });

                let imageResponse = {ok: true};

                if(imageFile) {
                    const formDataForImage = new FormData();

                    formDataForImage.append('file', imageFile);

                    imageResponse = await fetch(`${import.meta.env.VITE_API_URL}/event/upload`, {
                        method: 'POST',
                        credentials: 'include',
                        body: formDataForImage
                    })
                } 


                if (response.ok && imageResponse.ok) {
                    alert('Event updated successfully')
                    navigate('/admin/posts')
                } else {
                    alert('Error updating event')
                }
            

            } catch {
                setError("root", {
                    message: "The form did not submit correctly."
                })
            }

        } else {
            if (!imageFile) {
                setError("image", {
                    message: "Image is required for new events"
                });
                return;
            }
            
            try {
                const response = await fetch(`${import.meta.env.VITE_API_URL}/newEvent`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        title: formData.title,
                        date: formData.date,
                        time: formData.time,
                        location: formData.location,
                        cost: formData.cost,
                        image: imageFile.name,
                        archived: formData.archived,
                        details: formData.details,
                        deleted: false,
                        url_slug: formData.url_slug
                    })
                });

                const formDataForImage = new FormData();
                formDataForImage.append('file', imageFile);

                const imageResponse = await fetch(`${import.meta.env.VITE_API_URL}/event/upload`, {
                    method: 'POST',
                    credentials: 'include',
                    body: formDataForImage
                })

                if (response.ok && imageResponse.ok) {
                    alert('Event added successfully')
                    navigate('/admin/posts')
                } else {
                    alert('Error adding event')
                }

            } catch {
                setError("root", {
                    message: "The form did not submit correctly."
                })
            }
        }
        
    }


    return <>
        <div className="container">
            {loading ? (
                <div>"Loading"</div> 
            ) :  
            <>
                <h1>{!params.eventID ? "New" : "Edit" } Event</h1>
                <form 
                    className="new-post-form" action="" 
                    onSubmit={handleSubmit(onSubmit)}
                >         
                    <label>Title - <span className="instructions">Max Length 25 characters</span></label>
                    <input {...register("title", {
                        required: "true"
                    })} type="text" name="title" id="title" placeholder="Title" maxLength={25}/>

                    <label>Url slug <span className="instructions">Max Length 25 characters</span></label>
                    <input {...register("url_slug", {
                        required: "true"
                    })} 
                        type="text" 
                        name="url_slug" 
                        id="url_slug" 
                        placeholder="URL_SLUG" 
                        maxLength={25} 
                    />

                    <div className="form-date-time">
                        <label htmlFor="date">Date</label>
                        <label htmlFor="time">Time</label>
                        <input {...register("date", {
                            required:"true"
                        })} type="date" name="date" id="date" />
                        <input {...register("time", {
                            required:"true"
                        })} type="time" name="time" id="time" />

                    </div>
                    <div className="form-date-time">
                        <label htmlFor="location">Location</label>
                        <label htmlFor="cost">Cost</label>
                        <input {...register("location", {
                            required: "true"
                        })} type="text" name="location" id="location" placeholder="Enter closest address"/>
                        <input {...register("cost", {
                            required: "Cost is required"
                        })} type="text" name="cost" id="cost" placeholder="Free"/>
                        
                    </div>
                    <label>Current Image</label>
                    <div style={{ aspectRatio: '4 / 3', backgroundColor: 'silver', overflow: 'hidden', width: '66%', margin: '0 auto'}}>
                        
                        {params.eventID && 
                        <img
                            src={imageFile ? previewSrc : (params.eventID ? `${import.meta.env.VITE_API_URL}/uploads/${image}` : '')}
                            width='100%'
                            height='100%'
                            style={{ objectFit: 'cover' }}
                        />
}
                    </div>

                    <label htmlFor="image">Main Image</label>
                    <input 
                        type="file" 
                        {...register("image", {
                            validate: {
                                fileSize: (value) => {
                                if (!value[0]) return true;
                                return value[0].size <= 2000000 || "File size must be less than 2MB";
                                },
                                fileType: (value) => {
                                if (!value[0]) return true;
                                return (
                                    ["image/jpeg", "image/png", "image/gif"].includes(value[0].type) ||
                                    "Only JPEG, PNG, and GIF files are allowed"
                                );
                                },
                            },
                        }
                    )} name="image" id="image" onChange={handleImageChange}/>
                    
                    <label htmlFor="details">Details - <span className="instructions">Use markdown formatting <a href="https://www.markdownguide.org/basic-syntax/" target="blank">Guide</a></span></label>
                    <textarea 
                        {...register("details", {})} 
                        
                        name="details" 
                        id="details" 
                        rows={10} 
                        onChange={collectTextArea}
                    />
                    <label>Post Preview</label>
                    <div className="markdown-render">
                        <Markdown>{textArea}</Markdown>
                    </div>
                    
                    { eventData.id  
                    ?
                    <div>
                        <label htmlFor="archived">Archive? (Remove from website but don't delete)</label>
                        
                       <input {...register("archived")} 
                            type="checkbox" 
                            name="archived" 
                            id="archived" 
                        />

                    </div>
                    : <></>
                    }

                    {errors.cost && <div>{errors.cost.message}</div>}
                    <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Loading..." : "Submit" }
                    </button>
                    <div className='testing'>

                    
                    {archivedValue === true &&
                    <button 
                        type="button" 
                        disabled={isSubmitting}
                        className="delete-btn"
                        onClick={() => {
                          if(eventData.id){
                            deletePost(eventData.id)
                          }  

                        }    
                        }
                    >
                        {isSubmitting ? "Deleting..." : "Delete" }
                    </button>
                    }
                    </div>
                    {errors.root && <div>{errors.root.message}</div>}
                </form>
            </>
            }
        </div>
    
    </>
}